import { useState } from 'react';
import { useDeviceSize } from '../hooks/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import StepIndicator from '../components/diagnostic/StepIndicator';
import HousingType from '../components/diagnostic/steps/HousingType';
import Location from '../components/diagnostic/steps/Location';
import SecurityEquipment from '../components/diagnostic/steps/SecurityEquipment';
import Navigation from '../components/diagnostic/Navigation';
import { DiagnosticFormData } from '../types/diagnostic';
import StepTransition from '../components/diagnostic/steps/StepTransition';
import { dpsTheme } from '../styles/dpsTheme';

export default function DiagnosticForm() {
  const { isMobile } = useDeviceSize();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<DiagnosticFormData>({
    // Caractéristiques de base
    housingType: 'apartment',
    apartmentAccessType: '',
    occupancyStatus: '',
    floors: 0,
    hasBasement: false,
    hasGarage: false,
    garageConnectedToHome: null,
    hasAccessibleGarden: false,
    hasAccessibleBalcony: false,
    
    // Profil d'occupation
    occupationType: '',
    householdComposition: '',
    hasPet: false,
    presenceRhythm: '',
    
    // Caractéristiques architecturales avancées
    doorMaterial: '',
    windowType: '',
    hasShutters: '',
    sideAccess: '',
    
    // Environnement
    streetLighting: '',
    streetVisibility: '',
    neighborhoodType: '',
    
    // Localisation
    postalCode: '',
    city: '',
    
    // Équipements
    equipment: []
  });

  const updateFormData = (data: Partial<DiagnosticFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return Boolean(formData.housingType);
      case 2:
        return formData.postalCode.length === 5 && formData.city !== '';
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      navigate('/results', { state: { formData } });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: dpsTheme.colors.background.primary,
        paddingTop: '80px',
        paddingBottom: '60px',
        overflowX: 'hidden'
      }}
    >
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 16px', width: '100%', boxSizing: 'border-box' }}>
        <StepIndicator currentStep={currentStep} />

        <div
          style={{
            marginTop: dpsTheme.spacing['2xl'],
            background: dpsTheme.colors.background.card,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: dpsTheme.borderRadius.lg,
            padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing['3xl'],
            boxShadow: dpsTheme.shadows.card,
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <StepTransition>
            {currentStep === 1 && (
              <HousingType
                formData={formData}
                updateFormData={updateFormData}
              />
            )}

            {currentStep === 2 && (
              <Location
                formData={formData}
                updateFormData={updateFormData}
              />
            )}

            {currentStep === 3 && (
              <SecurityEquipment
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
          </StepTransition>

          <Navigation
            currentStep={currentStep}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isValid={isStepValid()}
          />
        </div>
      </div>
    </div>
  );
}